import React from 'react';
import {
  Box, Button, CircularProgress, Card, Typography, Chip,
  Table, TableHead, TableBody, TableRow, TableCell,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Snackbar, Alert,
} from '@mui/material';
import { ErrorOutline, WarningAmber, InfoOutlined } from '@mui/icons-material';
import { PageHeader, TableSurface, headerCellSx, bodyRowSx, StatusDot, TableEmptyState, StatusTone } from '../../components/common/pageKit';
import eximService from '../../services/eximService';
import treasuryService from '../../services/treasuryService';
import {
  EximDashboard, EligibleInvoice, ExportShipment, ImportShipment, ExportType,
} from '../../types/exim';
import { BankAccount } from '../../types/treasury';

// ---- local formatting helpers (mirrors src/pages/treasury/treasuryFormat.ts;
// duplicated rather than cross-imported since EXIM is not a treasury submodule,
// same "copy per page" convention already used for Section/cardSx elsewhere) ----
const fmtMoney = (v: number | null | undefined): string => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  return `${n < 0 ? '-' : ''}₹${Math.abs(Math.round(n)).toLocaleString('en-IN')}`;
};
const fmtMoneyCompact = (v: number | null | undefined): string => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  const n = Number(v); const a = Math.abs(n); const sign = n < 0 ? '-' : '';
  if (a >= 1e7) return `${sign}₹${(a / 1e7).toFixed(2)} Cr`;
  if (a >= 1e5) return `${sign}₹${(a / 1e5).toFixed(2)} L`;
  if (a >= 1e3) return `${sign}₹${(a / 1e3).toFixed(1)}K`;
  return `${sign}₹${Math.round(a)}`;
};
const fmtDate = (v: string | null | undefined): string => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const humanize = (s?: string | null): string => (s ? String(s).replace(/_/g, ' ') : '—');

const cardSx = { borderRadius: 3.5, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', transition: 'box-shadow .2s, transform .2s', '&:hover': { boxShadow: '0 12px 32px rgba(16,26,54,.09)', transform: 'translateY(-2px)' } };

const Section: React.FC<{ title: string; subtitle?: string; right?: React.ReactNode; children: React.ReactNode; sx?: any }> =
  ({ title, subtitle, right, children, sx }) => (
    <Card elevation={0} sx={{ ...cardSx, ...sx, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 2.25, pb: 0 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 720, fontSize: '0.98rem', letterSpacing: '-.01em' }}>{title}</Typography>
          {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
        </Box>
        {right && <Box sx={{ ml: 'auto' }}>{right}</Box>}
      </Box>
      <Box sx={{ p: 2.25, pt: 1.75, flexGrow: 1 }}>{children}</Box>
    </Card>
  );

const TONE_HEX: Record<string, string> = { critical: '#dc2626', warning: '#d9930b', info: '#0e8fd6' };
const EXPORT_STATUS_TONE: Record<string, StatusTone> = { DRAFT: 'neutral', SHIPPED: 'info', BRC_PENDING: 'warning', REALIZED: 'success', CLOSED: 'neutral' };
const IMPORT_STATUS_TONE: Record<string, StatusTone> = { DRAFT: 'neutral', IN_TRANSIT: 'info', CLEARED: 'success', CLOSED: 'neutral' };
const EXPORT_TYPES: ExportType[] = ['LUT', 'IGST_PAID', 'DEEMED_EXPORT'];
const CURRENCIES = ['USD', 'EUR', 'GBP', 'AED', 'SGD', 'JPY'];

const emptyExportForm = { salesInvoiceId: '', exportType: 'LUT' as ExportType, shippingBillNumber: '', shippingBillDate: '', portCode: '', incoterm: 'FOB', consigneeCountry: '', currency: 'USD', exchangeRate: '', fcAmount: '' };
const emptyImportForm = { purchaseInvoiceId: '', billOfEntryNumber: '', billOfEntryDate: '', portCode: '', incoterm: 'CIF', countryOfOrigin: '', currency: 'USD', exchangeRate: '', fcAmount: '', customsDutyAmount: '', igstOnImport: '' };
const emptyBrcForm = { brcNumber: '', realizationDate: new Date().toISOString().slice(0, 10), realizedFcAmount: '', exchangeRateRealized: '', bankAccountId: '' };
const emptyDutyForm = { amount: '', paymentDate: new Date().toISOString().slice(0, 10), bankAccountId: '', reference: '' };
const emptyProfileForm = { iecCode: '', adCode: '', lutNumber: '', lutValidFrom: '', lutValidUpto: '', defaultIncoterm: 'FOB', notes: '' };

const EximPage: React.FC = () => {
  const [dash, setDash] = React.useState<EximDashboard | null>(null);
  const [banks, setBanks] = React.useState<BankAccount[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [toast, setToast] = React.useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  const load = React.useCallback(() => {
    setLoading(true);
    Promise.all([eximService.getDashboard(), treasuryService.listBanks()])
      .then(([d, b]) => { setDash(d); setBanks(b.accounts || []); })
      .finally(() => setLoading(false));
  }, []);
  React.useEffect(() => { load(); }, [load]);

  // ---- Trade profile dialog ----
  const [profileOpen, setProfileOpen] = React.useState(false);
  const [profileForm, setProfileForm] = React.useState(emptyProfileForm);
  const [profileSaving, setProfileSaving] = React.useState(false);
  const openProfile = () => {
    const p = dash?.tradeProfile;
    setProfileForm({
      iecCode: p?.iec_code || '', adCode: p?.ad_code || '', lutNumber: p?.lut_number || '',
      lutValidFrom: p?.lut_valid_from ? String(p.lut_valid_from).slice(0, 10) : '',
      lutValidUpto: p?.lut_valid_upto ? String(p.lut_valid_upto).slice(0, 10) : '',
      defaultIncoterm: p?.default_incoterm || 'FOB', notes: p?.notes || '',
    });
    setProfileOpen(true);
  };
  const saveProfile = async () => {
    setProfileSaving(true);
    try {
      await eximService.upsertTradeProfile(profileForm);
      setToast({ msg: 'Trade profile saved', sev: 'success' });
      setProfileOpen(false); load();
    } catch (e: any) { setToast({ msg: e?.message || 'Could not save trade profile', sev: 'error' }); }
    finally { setProfileSaving(false); }
  };

  // ---- New export shipment dialog ----
  const [exportOpen, setExportOpen] = React.useState(false);
  const [eligibleSales, setEligibleSales] = React.useState<EligibleInvoice[]>([]);
  const [exportForm, setExportForm] = React.useState(emptyExportForm);
  const [exportSaving, setExportSaving] = React.useState(false);
  const openNewExport = async () => {
    setExportForm(emptyExportForm);
    setExportOpen(true);
    try { const r = await eximService.getEligibleSalesInvoices(); setEligibleSales(r.invoices || []); }
    catch (e: any) { setToast({ msg: e?.message || 'Could not load eligible invoices', sev: 'error' }); }
  };
  const submitExport = async () => {
    setExportSaving(true);
    try {
      await eximService.createExportShipment({
        salesInvoiceId: Number(exportForm.salesInvoiceId), exportType: exportForm.exportType,
        shippingBillNumber: exportForm.shippingBillNumber || undefined, shippingBillDate: exportForm.shippingBillDate || undefined,
        portCode: exportForm.portCode || undefined, incoterm: exportForm.incoterm || undefined,
        consigneeCountry: exportForm.consigneeCountry || undefined, currency: exportForm.currency,
        exchangeRate: Number(exportForm.exchangeRate) || 0, fcAmount: Number(exportForm.fcAmount) || 0,
      });
      setToast({ msg: 'Export shipment created', sev: 'success' });
      setExportOpen(false); load();
    } catch (e: any) { setToast({ msg: e?.message || 'Could not create export shipment', sev: 'error' }); }
    finally { setExportSaving(false); }
  };

  // ---- Record BRC dialog ----
  const [brcOpen, setBrcOpen] = React.useState(false);
  const [brcShipment, setBrcShipment] = React.useState<ExportShipment | null>(null);
  const [brcForm, setBrcForm] = React.useState(emptyBrcForm);
  const [brcSaving, setBrcSaving] = React.useState(false);
  const openBrc = (s: ExportShipment) => {
    setBrcShipment(s);
    setBrcForm({ ...emptyBrcForm, realizedFcAmount: String(s.fc_amount), exchangeRateRealized: String(s.exchange_rate) });
    setBrcOpen(true);
  };
  const submitBrc = async () => {
    if (!brcShipment) return;
    setBrcSaving(true);
    try {
      const r = await eximService.recordBrc(brcShipment.id, {
        brcNumber: brcForm.brcNumber, realizationDate: brcForm.realizationDate,
        realizedFcAmount: Number(brcForm.realizedFcAmount) || 0, exchangeRateRealized: Number(brcForm.exchangeRateRealized) || 0,
        bankAccountId: Number(brcForm.bankAccountId),
      });
      const gl = r.forexGainLoss;
      setToast({ msg: `BRC recorded${gl !== 0 ? ` — forex ${gl > 0 ? 'gain' : 'loss'} of ${fmtMoney(Math.abs(gl))}` : ''}.`, sev: 'success' });
      setBrcOpen(false); load();
    } catch (e: any) { setToast({ msg: e?.message || 'Could not record BRC', sev: 'error' }); }
    finally { setBrcSaving(false); }
  };

  // ---- New import shipment dialog ----
  const [importOpen, setImportOpen] = React.useState(false);
  const [eligiblePurchase, setEligiblePurchase] = React.useState<EligibleInvoice[]>([]);
  const [importForm, setImportForm] = React.useState(emptyImportForm);
  const [importSaving, setImportSaving] = React.useState(false);
  const openNewImport = async () => {
    setImportForm(emptyImportForm);
    setImportOpen(true);
    try { const r = await eximService.getEligiblePurchaseInvoices(); setEligiblePurchase(r.invoices || []); }
    catch (e: any) { setToast({ msg: e?.message || 'Could not load eligible invoices', sev: 'error' }); }
  };
  const submitImport = async () => {
    setImportSaving(true);
    try {
      await eximService.createImportShipment({
        purchaseInvoiceId: Number(importForm.purchaseInvoiceId), billOfEntryNumber: importForm.billOfEntryNumber || undefined,
        billOfEntryDate: importForm.billOfEntryDate || undefined, portCode: importForm.portCode || undefined,
        incoterm: importForm.incoterm || undefined, countryOfOrigin: importForm.countryOfOrigin || undefined,
        currency: importForm.currency, exchangeRate: Number(importForm.exchangeRate) || 0, fcAmount: Number(importForm.fcAmount) || 0,
        customsDutyAmount: Number(importForm.customsDutyAmount) || undefined, igstOnImport: Number(importForm.igstOnImport) || undefined,
      });
      setToast({ msg: 'Import shipment created', sev: 'success' });
      setImportOpen(false); load();
    } catch (e: any) { setToast({ msg: e?.message || 'Could not create import shipment', sev: 'error' }); }
    finally { setImportSaving(false); }
  };

  // ---- Record customs duty dialog ----
  const [dutyOpen, setDutyOpen] = React.useState(false);
  const [dutyShipment, setDutyShipment] = React.useState<ImportShipment | null>(null);
  const [dutyForm, setDutyForm] = React.useState(emptyDutyForm);
  const [dutySaving, setDutySaving] = React.useState(false);
  const openDuty = (s: ImportShipment) => {
    setDutyShipment(s);
    setDutyForm({ ...emptyDutyForm, amount: String(s.customs_duty_amount || '') });
    setDutyOpen(true);
  };
  const submitDuty = async () => {
    if (!dutyShipment) return;
    setDutySaving(true);
    try {
      await eximService.recordCustomsDuty(dutyShipment.id, {
        amount: Number(dutyForm.amount) || 0, paymentDate: dutyForm.paymentDate,
        bankAccountId: Number(dutyForm.bankAccountId), reference: dutyForm.reference || undefined,
      });
      setToast({ msg: 'Customs duty recorded', sev: 'success' });
      setDutyOpen(false); load();
    } catch (e: any) { setToast({ msg: e?.message || 'Could not record customs duty', sev: 'error' }); }
    finally { setDutySaving(false); }
  };

  const headerActions = (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Button variant="outlined" size="small" onClick={openProfile}>Trade Profile</Button>
      <Button variant="outlined" size="small" onClick={openNewImport}>New Import Shipment</Button>
      <Button variant="contained" size="small" onClick={openNewExport}>New Export Shipment</Button>
    </Box>
  );

  if (loading || !dash) {
    return (
      <Box>
        <PageHeader title="Import / Export Compliance" subtitle="IEC/LUT master, export shipments, import bills, customs duty and forex settlement." actions={headerActions} />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}><CircularProgress size={30} /></Box>
      </Box>
    );
  }

  const { tradeProfile, lutStatus, kpis, exportShipments, importShipments, alerts } = dash;
  const activeBanks = banks.filter((b) => b.is_active);

  const KPIS: Array<{ label: string; render: React.ReactNode; color: string }> = [
    { label: 'Export Shipments', render: kpis.exportShipmentCount, color: '#2a78d6' },
    { label: 'Import Shipments', render: kpis.importShipmentCount, color: '#eb6834' },
    { label: 'BRC Pending', render: `${kpis.pendingBrcCount} · ${fmtMoneyCompact(kpis.pendingBrcInr)}`, color: '#d9930b' },
    { label: 'Forex Realized YTD', render: fmtMoneyCompact(kpis.forexRealizedYtd), color: '#0891b2' },
    { label: 'Forex Gain/Loss YTD', render: `${kpis.forexGainLossYtd >= 0 ? '+' : ''}${fmtMoneyCompact(kpis.forexGainLossYtd)}`, color: kpis.forexGainLossYtd >= 0 ? '#12a150' : '#dc2626' },
    { label: 'Customs Duty YTD', render: fmtMoneyCompact(kpis.customsDutyYtd), color: '#4a3aa7' },
    { label: 'Duty Unpaid', render: `${kpis.dutyUnpaidCount} · ${fmtMoneyCompact(kpis.dutyUnpaidInr)}`, color: '#dc2626' },
  ];

  return (
    <Box>
      <PageHeader title="Import / Export Compliance" subtitle="IEC/LUT master, export shipments, import bills, customs duty and forex settlement." actions={headerActions} />

      {/* KPI STRIP */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(4,1fr)', lg: 'repeat(7,1fr)' }, gap: 2, mb: 2 }}>
        {KPIS.map((k) => (
          <Card key={k.label} elevation={0} sx={{ ...cardSx, p: 1.5 }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 780, letterSpacing: '-.02em', lineHeight: 1.2, color: k.color, fontVariantNumeric: 'tabular-nums' }}>{k.render}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1.2, display: 'block', mt: 0.5 }}>{k.label}</Typography>
          </Card>
        ))}
      </Box>

      {/* ALERTS */}
      {alerts.length > 0 && (
        <Section title="Compliance Alerts" subtitle="LUT validity, pending forex settlement and unpaid customs duty" sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {alerts.map((a, i) => {
              const c = TONE_HEX[a.severity] || '#64748b';
              return (
                <Box key={i} sx={{ border: '1px solid', borderColor: 'divider', borderLeft: `3px solid ${c}`, borderRadius: 2.5, p: 1.5, display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                  {a.severity === 'critical' ? <ErrorOutline sx={{ fontSize: 18, color: c, mt: '1px' }} /> : a.severity === 'warning' ? <WarningAmber sx={{ fontSize: 18, color: c, mt: '1px' }} /> : <InfoOutlined sx={{ fontSize: 18, color: c, mt: '1px' }} />}
                  <Box>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{a.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{a.detail}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Section>
      )}

      {/* TRADE PROFILE */}
      <Section title="Trade Profile" subtitle="IEC, AD code and LUT — the master data customs clearance and zero-rated exports depend on" sx={{ mb: 2 }}
        right={<Button size="small" variant="outlined" onClick={openProfile}>Edit</Button>}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(4,1fr)' }, gap: 1.5 }}>
          <Box sx={{ p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 2.5 }}>
            <Typography variant="caption" color="text.secondary">IEC Code</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 700, fontFamily: 'monospace' }}>{tradeProfile?.iec_code || '—'}</Typography>
          </Box>
          <Box sx={{ p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 2.5 }}>
            <Typography variant="caption" color="text.secondary">AD Code</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 700, fontFamily: 'monospace' }}>{tradeProfile?.ad_code || '—'}</Typography>
          </Box>
          <Box sx={{ p: 1.25, border: '1px solid', borderColor: lutStatus?.expired ? '#dc2626' : 'divider', borderRadius: 2.5 }}>
            <Typography variant="caption" color="text.secondary">LUT</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{tradeProfile?.lut_number || '—'}</Typography>
            {lutStatus && (
              <Typography variant="caption" sx={{ color: lutStatus.expired ? '#dc2626' : lutStatus.daysRemaining <= 30 ? '#d9930b' : 'text.disabled' }}>
                {lutStatus.expired ? `Expired ${fmtDate(lutStatus.validUpto)}` : `Valid till ${fmtDate(lutStatus.validUpto)} (${lutStatus.daysRemaining}d)`}
              </Typography>
            )}
          </Box>
          <Box sx={{ p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 2.5 }}>
            <Typography variant="caption" color="text.secondary">Default Incoterm</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{tradeProfile?.default_incoterm || '—'}</Typography>
          </Box>
        </Box>
      </Section>

      {/* EXPORT SHIPMENTS */}
      <Section title="Export Shipments" subtitle="Shipping bill, LUT/IGST basis and forex settlement, per sales invoice" sx={{ mb: 2 }}
        right={<Button size="small" variant="outlined" onClick={openNewExport}>New Export Shipment</Button>}>
        <TableSurface>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Shipment</TableCell>
                <TableCell sx={headerCellSx}>Invoice / Customer</TableCell>
                <TableCell sx={headerCellSx}>Type</TableCell>
                <TableCell sx={headerCellSx}>Shipping Bill</TableCell>
                <TableCell sx={headerCellSx} align="right">FC Amount</TableCell>
                <TableCell sx={headerCellSx} align="right">INR Value</TableCell>
                <TableCell sx={headerCellSx}>Status</TableCell>
                <TableCell sx={headerCellSx} />
              </TableRow>
            </TableHead>
            <TableBody>
              {exportShipments.map((s) => (
                <TableRow key={s.id} sx={bodyRowSx}>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{s.shipment_number}</TableCell>
                  <TableCell>
                    <Box sx={{ fontWeight: 650, fontSize: 12.5 }}>{s.invoice_number}</Box>
                    <Box sx={{ fontSize: 10, color: 'text.disabled' }}>{s.customer_name}</Box>
                  </TableCell>
                  <TableCell><Chip size="small" label={humanize(s.export_type)} variant="outlined" sx={{ height: 20, fontSize: 10 }} /></TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{s.shipping_bill_number || '—'}{s.shipping_bill_date ? ` · ${fmtDate(s.shipping_bill_date)}` : ''}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{s.currency} {Number(s.fc_amount).toLocaleString('en-IN')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 650, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(s.inr_amount)}</TableCell>
                  <TableCell><StatusDot label={humanize(s.status)} tone={EXPORT_STATUS_TONE[s.status] || 'neutral'} /></TableCell>
                  <TableCell>
                    {s.status !== 'REALIZED' && s.status !== 'CLOSED' && (
                      <Button size="small" variant="outlined" sx={{ fontSize: 11 }} onClick={() => openBrc(s)}>Record BRC</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {exportShipments.length === 0 && <TableEmptyState title="No export shipments yet" hint="Create one against an existing sales invoice to start tracking shipping-bill and forex-settlement compliance." />}
        </TableSurface>
      </Section>

      {/* IMPORT SHIPMENTS */}
      <Section title="Import Shipments" subtitle="Bill of entry, customs duty and country of origin, per purchase invoice" sx={{ mb: 2 }}
        right={<Button size="small" variant="outlined" onClick={openNewImport}>New Import Shipment</Button>}>
        <TableSurface>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Shipment</TableCell>
                <TableCell sx={headerCellSx}>Invoice / Vendor</TableCell>
                <TableCell sx={headerCellSx}>Bill of Entry</TableCell>
                <TableCell sx={headerCellSx} align="right">FC Amount</TableCell>
                <TableCell sx={headerCellSx} align="right">INR Value</TableCell>
                <TableCell sx={headerCellSx} align="right">Customs Duty</TableCell>
                <TableCell sx={headerCellSx}>Status</TableCell>
                <TableCell sx={headerCellSx} />
              </TableRow>
            </TableHead>
            <TableBody>
              {importShipments.map((s) => (
                <TableRow key={s.id} sx={bodyRowSx}>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{s.shipment_number}</TableCell>
                  <TableCell>
                    <Box sx={{ fontWeight: 650, fontSize: 12.5 }}>{s.invoice_number}</Box>
                    <Box sx={{ fontSize: 10, color: 'text.disabled' }}>{s.vendor_name}</Box>
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{s.bill_of_entry_number || '—'}{s.bill_of_entry_date ? ` · ${fmtDate(s.bill_of_entry_date)}` : ''}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{s.currency} {Number(s.fc_amount).toLocaleString('en-IN')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 650, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(s.inr_amount)}</TableCell>
                  <TableCell align="right" sx={{ color: s.duty_paid ? 'success.main' : 'error.main', fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(s.customs_duty_amount)}</TableCell>
                  <TableCell><StatusDot label={humanize(s.status)} tone={IMPORT_STATUS_TONE[s.status] || 'neutral'} /></TableCell>
                  <TableCell>
                    {!s.duty_paid && (
                      <Button size="small" variant="outlined" sx={{ fontSize: 11 }} onClick={() => openDuty(s)}>Record Duty</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {importShipments.length === 0 && <TableEmptyState title="No import shipments yet" hint="Create one against an existing purchase invoice to start tracking bill-of-entry and customs-duty compliance." />}
        </TableSurface>
      </Section>

      {/* TRADE PROFILE DIALOG */}
      <Dialog open={profileOpen} onClose={() => !profileSaving && setProfileOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Trade profile</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
            <TextField label="IEC code" size="small" value={profileForm.iecCode} onChange={(e) => setProfileForm((f) => ({ ...f, iecCode: e.target.value }))} />
            <TextField label="AD code" size="small" value={profileForm.adCode} onChange={(e) => setProfileForm((f) => ({ ...f, adCode: e.target.value }))} />
            <TextField label="LUT number" size="small" value={profileForm.lutNumber} onChange={(e) => setProfileForm((f) => ({ ...f, lutNumber: e.target.value }))} />
            <TextField label="LUT valid from" type="date" size="small" InputLabelProps={{ shrink: true }} value={profileForm.lutValidFrom} onChange={(e) => setProfileForm((f) => ({ ...f, lutValidFrom: e.target.value }))} />
            <TextField label="LUT valid upto" type="date" size="small" InputLabelProps={{ shrink: true }} value={profileForm.lutValidUpto} onChange={(e) => setProfileForm((f) => ({ ...f, lutValidUpto: e.target.value }))} />
            <TextField label="Default Incoterm" size="small" value={profileForm.defaultIncoterm} onChange={(e) => setProfileForm((f) => ({ ...f, defaultIncoterm: e.target.value }))} />
            <TextField label="Notes" size="small" multiline minRows={2} value={profileForm.notes} onChange={(e) => setProfileForm((f) => ({ ...f, notes: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileOpen(false)} disabled={profileSaving}>Cancel</Button>
          <Button variant="contained" onClick={saveProfile} disabled={profileSaving}>{profileSaving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* NEW EXPORT SHIPMENT DIALOG */}
      <Dialog open={exportOpen} onClose={() => !exportSaving && setExportOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New export shipment</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
            <TextField select label="Sales invoice" size="small" value={exportForm.salesInvoiceId} onChange={(e) => setExportForm((f) => ({ ...f, salesInvoiceId: e.target.value }))}
              helperText={eligibleSales.length === 0 ? 'No invoices without a shipment yet' : ' '}>
              {eligibleSales.map((inv) => (
                <MenuItem key={inv.id} value={String(inv.id)}>{inv.invoice_number} · {inv.customer_name} · {fmtMoney(inv.total_amount)}</MenuItem>
              ))}
            </TextField>
            <TextField select label="Export type" size="small" value={exportForm.exportType} onChange={(e) => setExportForm((f) => ({ ...f, exportType: e.target.value as ExportType }))}>
              {EXPORT_TYPES.map((t) => <MenuItem key={t} value={t}>{humanize(t)}</MenuItem>)}
            </TextField>
            <TextField label="Shipping bill number" size="small" value={exportForm.shippingBillNumber} onChange={(e) => setExportForm((f) => ({ ...f, shippingBillNumber: e.target.value }))} />
            <TextField label="Shipping bill date" type="date" size="small" InputLabelProps={{ shrink: true }} value={exportForm.shippingBillDate} onChange={(e) => setExportForm((f) => ({ ...f, shippingBillDate: e.target.value }))} />
            <TextField label="Port code" size="small" value={exportForm.portCode} onChange={(e) => setExportForm((f) => ({ ...f, portCode: e.target.value }))} />
            <TextField label="Incoterm" size="small" value={exportForm.incoterm} onChange={(e) => setExportForm((f) => ({ ...f, incoterm: e.target.value }))} />
            <TextField label="Consignee country" size="small" value={exportForm.consigneeCountry} onChange={(e) => setExportForm((f) => ({ ...f, consigneeCountry: e.target.value }))} />
            <TextField select label="Currency" size="small" value={exportForm.currency} onChange={(e) => setExportForm((f) => ({ ...f, currency: e.target.value }))}>
              {CURRENCIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <TextField label="Exchange rate (to INR)" type="number" size="small" value={exportForm.exchangeRate} onChange={(e) => setExportForm((f) => ({ ...f, exchangeRate: e.target.value }))} />
            <TextField label="Invoice value (foreign currency)" type="number" size="small" value={exportForm.fcAmount} onChange={(e) => setExportForm((f) => ({ ...f, fcAmount: e.target.value }))}
              helperText={Number(exportForm.exchangeRate) > 0 && Number(exportForm.fcAmount) > 0 ? `≈ ${fmtMoney(Number(exportForm.exchangeRate) * Number(exportForm.fcAmount))}` : ' '} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportOpen(false)} disabled={exportSaving}>Cancel</Button>
          <Button variant="contained" onClick={submitExport} disabled={exportSaving || !exportForm.salesInvoiceId || !(Number(exportForm.exchangeRate) > 0) || !(Number(exportForm.fcAmount) > 0)}>
            {exportSaving ? 'Creating…' : 'Create shipment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* RECORD BRC DIALOG */}
      <Dialog open={brcOpen} onClose={() => !brcSaving && setBrcOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Record Bank Realisation Certificate</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
            {brcShipment && (
              <Typography variant="caption" color="text.secondary">
                Against {brcShipment.shipment_number} ({brcShipment.invoice_number}) — booked at {fmtMoney(brcShipment.inr_amount)}
              </Typography>
            )}
            <TextField label="BRC number" size="small" value={brcForm.brcNumber} onChange={(e) => setBrcForm((f) => ({ ...f, brcNumber: e.target.value }))} />
            <TextField label="Realization date" type="date" size="small" InputLabelProps={{ shrink: true }} value={brcForm.realizationDate} onChange={(e) => setBrcForm((f) => ({ ...f, realizationDate: e.target.value }))} />
            <TextField label={`Realized amount (${brcShipment?.currency || 'FC'})`} type="number" size="small" value={brcForm.realizedFcAmount} onChange={(e) => setBrcForm((f) => ({ ...f, realizedFcAmount: e.target.value }))} />
            <TextField label="Exchange rate realized" type="number" size="small" value={brcForm.exchangeRateRealized} onChange={(e) => setBrcForm((f) => ({ ...f, exchangeRateRealized: e.target.value }))}
              helperText={
                Number(brcForm.realizedFcAmount) > 0 && Number(brcForm.exchangeRateRealized) > 0 && brcShipment
                  ? (() => {
                      const realized = Number(brcForm.realizedFcAmount) * Number(brcForm.exchangeRateRealized);
                      const diff = realized - Number(brcShipment.inr_amount);
                      return `≈ ${fmtMoney(realized)} · forex ${diff >= 0 ? 'gain' : 'loss'} ${fmtMoney(Math.abs(diff))}`;
                    })()
                  : ' '
              } />
            <TextField select label="Receiving bank account" size="small" value={brcForm.bankAccountId} onChange={(e) => setBrcForm((f) => ({ ...f, bankAccountId: e.target.value }))}>
              {activeBanks.map((b) => <MenuItem key={b.id} value={b.id}>{b.account_name} · {b.bank_name}</MenuItem>)}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBrcOpen(false)} disabled={brcSaving}>Cancel</Button>
          <Button variant="contained" onClick={submitBrc} disabled={brcSaving || !brcForm.brcNumber || !(Number(brcForm.realizedFcAmount) > 0) || !(Number(brcForm.exchangeRateRealized) > 0) || !brcForm.bankAccountId}>
            {brcSaving ? 'Recording…' : 'Record BRC'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* NEW IMPORT SHIPMENT DIALOG */}
      <Dialog open={importOpen} onClose={() => !importSaving && setImportOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New import shipment</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
            <TextField select label="Purchase invoice" size="small" value={importForm.purchaseInvoiceId} onChange={(e) => setImportForm((f) => ({ ...f, purchaseInvoiceId: e.target.value }))}
              helperText={eligiblePurchase.length === 0 ? 'No invoices without a shipment yet' : ' '}>
              {eligiblePurchase.map((inv) => (
                <MenuItem key={inv.id} value={String(inv.id)}>{inv.invoice_number} · {inv.vendor_name} · {fmtMoney(inv.total_amount)}</MenuItem>
              ))}
            </TextField>
            <TextField label="Bill of entry number" size="small" value={importForm.billOfEntryNumber} onChange={(e) => setImportForm((f) => ({ ...f, billOfEntryNumber: e.target.value }))} />
            <TextField label="Bill of entry date" type="date" size="small" InputLabelProps={{ shrink: true }} value={importForm.billOfEntryDate} onChange={(e) => setImportForm((f) => ({ ...f, billOfEntryDate: e.target.value }))} />
            <TextField label="Port code" size="small" value={importForm.portCode} onChange={(e) => setImportForm((f) => ({ ...f, portCode: e.target.value }))} />
            <TextField label="Incoterm" size="small" value={importForm.incoterm} onChange={(e) => setImportForm((f) => ({ ...f, incoterm: e.target.value }))} />
            <TextField label="Country of origin" size="small" value={importForm.countryOfOrigin} onChange={(e) => setImportForm((f) => ({ ...f, countryOfOrigin: e.target.value }))} />
            <TextField select label="Currency" size="small" value={importForm.currency} onChange={(e) => setImportForm((f) => ({ ...f, currency: e.target.value }))}>
              {CURRENCIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <TextField label="Exchange rate (to INR)" type="number" size="small" value={importForm.exchangeRate} onChange={(e) => setImportForm((f) => ({ ...f, exchangeRate: e.target.value }))} />
            <TextField label="Invoice value (foreign currency)" type="number" size="small" value={importForm.fcAmount} onChange={(e) => setImportForm((f) => ({ ...f, fcAmount: e.target.value }))}
              helperText={Number(importForm.exchangeRate) > 0 && Number(importForm.fcAmount) > 0 ? `≈ ${fmtMoney(Number(importForm.exchangeRate) * Number(importForm.fcAmount))}` : ' '} />
            <TextField label="Customs duty (estimate, optional)" type="number" size="small" value={importForm.customsDutyAmount} onChange={(e) => setImportForm((f) => ({ ...f, customsDutyAmount: e.target.value }))} />
            <TextField label="IGST on import (optional)" type="number" size="small" value={importForm.igstOnImport} onChange={(e) => setImportForm((f) => ({ ...f, igstOnImport: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)} disabled={importSaving}>Cancel</Button>
          <Button variant="contained" onClick={submitImport} disabled={importSaving || !importForm.purchaseInvoiceId || !(Number(importForm.exchangeRate) > 0) || !(Number(importForm.fcAmount) > 0)}>
            {importSaving ? 'Creating…' : 'Create shipment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* RECORD CUSTOMS DUTY DIALOG */}
      <Dialog open={dutyOpen} onClose={() => !dutySaving && setDutyOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Record customs duty payment</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
            {dutyShipment && (
              <Typography variant="caption" color="text.secondary">Against {dutyShipment.shipment_number} ({dutyShipment.invoice_number})</Typography>
            )}
            <TextField label="Duty amount" type="number" size="small" value={dutyForm.amount} onChange={(e) => setDutyForm((f) => ({ ...f, amount: e.target.value }))} />
            <TextField label="Payment date" type="date" size="small" InputLabelProps={{ shrink: true }} value={dutyForm.paymentDate} onChange={(e) => setDutyForm((f) => ({ ...f, paymentDate: e.target.value }))} />
            <TextField select label="Paid from bank account" size="small" value={dutyForm.bankAccountId} onChange={(e) => setDutyForm((f) => ({ ...f, bankAccountId: e.target.value }))}>
              {activeBanks.map((b) => <MenuItem key={b.id} value={b.id}>{b.account_name} · {b.bank_name}</MenuItem>)}
            </TextField>
            <TextField label="Reference (optional)" size="small" value={dutyForm.reference} onChange={(e) => setDutyForm((f) => ({ ...f, reference: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDutyOpen(false)} disabled={dutySaving}>Cancel</Button>
          <Button variant="contained" onClick={submitDuty} disabled={dutySaving || !(Number(dutyForm.amount) > 0) || !dutyForm.bankAccountId}>
            {dutySaving ? 'Recording…' : 'Record duty'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4500} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default EximPage;
